import { Component, input, signal } from '@angular/core';
import { DatePipe, DecimalPipe, formatNumber } from '@angular/common';
import { BookedExtension, ContractRevision } from './contracts.service';
import { extensionDisplayName } from './extension-name.util';
import { CappedListComponent } from '../../shared/capped-list/capped-list.component';

@Component({
  selector: 'cmt-revision-list',
  imports: [DatePipe, DecimalPipe, CappedListComponent],
  templateUrl: './revision-list.component.html',
  styleUrl: './revision-list.component.scss',
})
export class RevisionListComponent {
  readonly revisions = input.required<ContractRevision[]>();
  readonly currency = input.required<string>();
  readonly activeIndex = input<number>(-1);
  readonly featureNames = input<Record<string, string>>({});
  /** Extension display names keyed by labelKey (category pricePackageName). */
  readonly extensionNames = input<Record<string, string>>({});

  /** Track which past revisions are manually expanded */
  readonly expandedIndices = signal<Set<number>>(new Set());

  isExpanded(index: number): boolean {
    return index === this.activeIndex() || this.expandedIndices().has(index);
  }

  toggleRevision(index: number): void {
    if (index === this.activeIndex()) return;
    const current = new Set(this.expandedIndices());
    if (current.has(index)) {
      current.delete(index);
    } else {
      current.add(index);
    }
    this.expandedIndices.set(current);
  }

  getFeatureDisplayName(techName: string): string {
    return this.featureNames()[techName] || '';
  }

  /**
   * Localized extension name (EB-4006): `pricePackageName[labelKey]` → `labelKey`.
   *
   * The `→ name` step this comment used to advertise is exactly what EB-4143 removed, and
   * this is the only consumer of the changed function (PR #42 review, roundup #9).
   */
  extensionLabel(ext: BookedExtension): string {
    return extensionDisplayName(this.extensionNames(), ext.extensionPriceInfo.labelKey);
  }

  /** Booked extensions sorted by their display name (old-CMT parity). */
  sortedExtensions(revision: ContractRevision): BookedExtension[] {
    return [...revision.bookedExtensions].sort((a, b) =>
      this.extensionLabel(a).localeCompare(this.extensionLabel(b)),
    );
  }

  /**
   * Plan price after discount, as old CMT displays it (`monthlyFee * (1 - discount)`).
   * Unlike old CMT, `revision.price` stays the list price here: the Change-Discount dialog
   * takes it as its upper bound (`contract-panel` passes `monthlyFee: rev.price`).
   */
  planPrice(revision: ContractRevision): number {
    return revision.price * (1 - (revision.discount ?? 0));
  }

  formatDiscount(discount: number): string {
    // `!discount` already covers 0 — the second clause was unreachable (roundup #26).
    if (!discount) return '';
    // Negative = surcharge (price above list); shown as-is like old CMT (`number: 0`), e.g. "-1,900%".
    return `${formatNumber(discount * 100, 'en', '1.0-0')}% discount`;
  }
}
